import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicApi, getImageUrl } from '../lib/api';
import { downloadPdfWithCache } from '../lib/pdf';
import { devError } from '../lib/logger';
import { iterateSectionCards } from '../lib/grouping';
import { Button } from '../components/ui/button';
import HTMLFlipBook from 'react-pageflip';
import { toast } from 'sonner';
import { Download, ChevronLeft, ChevronRight, Maximize2, Minimize2, BookOpen, Grid3X3, Image as ImageIcon, Loader2 } from 'lucide-react';
import '@/App.css';

function CoverPage({ catalog }) {
  const branding = catalog.branding || {};
  const primaryColor = branding.primary_color || '#C5A059';
  const tagline = branding.tagline || 'Gold Foil Artwork Collection';
  const coverImg = catalog.cover_image ? getImageUrl(catalog.cover_image) : null;
  const corp = catalog.catalog_type === 'corporate' ? (catalog.corporate_meta || {}) : null;
  return (
    <div className="w-full h-full bg-[#FDFBF7] flex flex-col">
      <div className="relative bg-[#141414] py-6 px-4 text-center" style={{ borderLeft: `3px solid ${primaryColor}`, borderRight: `3px solid ${primaryColor}` }}>
        <img src={branding.logo_url ? getImageUrl(branding.logo_url) : '/brand/aarya-logo-gold-on-black.png'} alt="AARYA24kt" className="h-14 mx-auto object-contain" />
      </div>
      <div className="px-5 pt-5 pb-3 text-center">
        <h1 className="font-serif text-2xl font-bold leading-tight" style={{ color: '#4a3a1f' }}>{catalog.title}</h1>
        {catalog._title_trans && <p className="font-serif text-base mt-1" style={{ color: '#8a6b30' }}>{catalog._title_trans}</p>}
        {catalog.subtitle && <p className="text-[10px] tracking-[0.25em] uppercase mt-2 text-[#5C5C5C]">{catalog.subtitle}</p>}
        {catalog._subtitle_trans && <p className="text-[10px] italic text-[#888] mt-0.5">{catalog._subtitle_trans}</p>}
        <div className="w-12 h-px mx-auto my-2.5" style={{ background: primaryColor }} />
        {catalog.intro_note && <p className="text-[10px] text-[#5C5C5C] max-w-[260px] mx-auto leading-snug line-clamp-3">{catalog.intro_note}</p>}
        {catalog._intro_trans && <p className="text-[9px] italic text-[#888] max-w-[260px] mx-auto leading-snug line-clamp-2 mt-0.5">{catalog._intro_trans}</p>}
        {corp && (corp.client_company || corp.validity_date) && (
          <div className="mt-3 pt-2 border-t border-[#E5E0D8] text-[9px] text-[#5C5C5C] leading-relaxed">
            {corp.client_company && <p>Prepared for: <strong style={{ color: primaryColor }}>{corp.client_company}</strong></p>}
            {corp.validity_date && <p className="mt-0.5">Valid until: {corp.validity_date}</p>}
          </div>
        )}
      </div>
      {/* iter28: bounded cover — object-contain, ~50% of flipbook page height,
          soft backdrop so aspect ratio is preserved and the layout feels premium. */}
      <div className="relative bg-[#F5F2EB] overflow-hidden flex items-center justify-center px-4 py-4" style={{ height: '50%' }}>
        {coverImg ? (
          <img src={coverImg} alt="Cover" className="block mx-auto object-contain w-auto h-auto max-w-[85%] max-h-full" />
        ) : (
          <div className="w-[75%] h-full flex items-center justify-center font-serif text-2xl tracking-[0.3em] rounded-sm" style={{ background: 'linear-gradient(135deg, #2a1f10 0%, #141414 100%)', color: primaryColor }}>
            {branding.company_name || 'AARYA24kt'}
          </div>
        )}
      </div>
      {tagline && <p className="text-[8px] text-center text-[#888] italic py-1.5 bg-[#FDFBF7]">{tagline}</p>}
    </div>
  );
}

function TocPage({ catalog }) {
  const branding = catalog.branding || {};
  const primaryColor = branding.primary_color || '#C5A059';
  return (
    <div className="w-full h-full bg-[#FDFBF7] p-10">
      <h2 className="font-serif text-2xl text-center mb-8" style={{ color: primaryColor }}>Contents</h2>
      <div className="space-y-3">
        {catalog.sections?.map((s) => (
          <div key={s.id} className="flex justify-between items-start py-2 border-b border-[#E5E0D8]">
            <div className="min-w-0 pr-3">
              <p className="text-sm text-[#1A1A1A] truncate">{s.name}</p>
              {s._name_trans && <p className="text-[10px] text-[#888] italic truncate">{s._name_trans}</p>}
            </div>
            <span className="text-xs text-[#5C5C5C] shrink-0">{s.products?.length || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionDividerPage({ name, nameTrans, primaryColor }) {
  return (
    <div className="w-full h-full bg-[#FDFBF7] flex flex-col items-center justify-center px-6 text-center">
      <div className="w-12 h-px mb-4" style={{ background: primaryColor }} />
      <h2 className="font-serif text-2xl" style={{ color: primaryColor }}>{name}</h2>
      {nameTrans && <p className="text-sm italic text-[#888] mt-2">{nameTrans}</p>}
      <div className="w-12 h-px mt-4" style={{ background: primaryColor }} />
    </div>
  );
}

function ProductPage({ product, primaryColor, isCorporate, showPrices, showQuantity, isGroupCard }) {
  const pt = product._translations || {};
  const tiers = (isCorporate && showPrices) ? (product._moq_tiers || []) : [];
  // Product Master V2 (iter24): group-card overrides — display parent
  // image / code / name / description in place of the SKU's fields and
  // replace the size+MRP row with an "Available Sizes" table below.
  const group = product._group || null;
  const siblings = (isGroupCard && product._group_siblings) || [];
  const displayCode = isGroupCard ? (group?.code || product.product_code) : product.product_code;
  const displayName = isGroupCard ? (group?.name || product.product_name) : product.product_name;
  const displayShort = isGroupCard ? (group?.short_description || product.short_description) : product.short_description;
  const displayImage = isGroupCard ? (group?.image || product.cover_image) : product.cover_image;
  return (
    <div className="w-full h-full bg-[#FDFBF7] flex flex-col p-6">
      <div className={`${(tiers.length || siblings.length) ? 'h-40' : 'flex-1'} flex items-center justify-center bg-white border border-[#E5E0D8] rounded-sm overflow-hidden mb-4`}>
        {displayImage ? (
          <img src={getImageUrl(displayImage)} alt={displayName} className="max-w-full max-h-full object-contain" />
        ) : (
          <ImageIcon size={48} className="text-[#E5E0D8]" />
        )}
      </div>
      <div className="border-t border-[#E5E0D8] pt-3">
        <p className="text-[9px] tracking-[0.2em] uppercase mb-1" style={{ color: primaryColor }}>{displayCode}</p>
        <h3 className="text-sm font-medium text-[#1A1A1A]">{displayName}</h3>
        {!isGroupCard && pt.product_name && <p className="text-xs italic text-[#888] line-clamp-2">{pt.product_name}</p>}
        {displayShort && <p className="text-[10px] text-[#5C5C5C] line-clamp-2 mt-1">{displayShort}</p>}
        {!isGroupCard && pt.short_description && <p className="text-[9px] italic text-[#aaa] line-clamp-2">{pt.short_description}</p>}
        {isGroupCard ? (
          <div className="mt-2 pt-2 border-t border-[#E5E0D8]">
            <p className="text-[8px] tracking-[0.15em] uppercase font-semibold mb-1" style={{ color: primaryColor }}>Available Sizes</p>
            <table className="w-full text-[9px] border-collapse">
              <thead>
                <tr style={{ borderBottom: `1px solid ${primaryColor}` }}>
                  <th className="text-left py-0.5 pr-1 font-semibold text-[#1A1A1A]">SKU</th>
                  <th className="text-left py-0.5 pr-1 font-semibold text-[#1A1A1A]">Size</th>
                  {showPrices && <th className="text-right py-0.5 font-semibold text-[#1A1A1A]">MRP</th>}
                  {showQuantity && <th className="text-right py-0.5 pl-1 font-semibold text-[#1A1A1A]">Qty</th>}
                  {showQuantity && <th className="text-right py-0.5 pl-1 font-semibold text-[#1A1A1A]">Qty</th>}
                </tr>
              </thead>
              <tbody>
                {siblings.slice(0, 8).map((sib) => (
                  <tr key={sib.product_code} className="border-b border-[#F0EBE2] last:border-b-0">
                    <td className="py-0.5 pr-1 font-mono text-[9px] whitespace-nowrap" style={{ color: primaryColor }}>{sib.product_code}</td>
                    <td className="py-0.5 pr-1 text-[#5C5C5C] whitespace-nowrap">{sib.size || '—'}</td>
                    {showPrices && <td className="py-0.5 text-right font-medium text-[#1A1A1A] whitespace-nowrap">{sib._display_price || '—'}</td>}
                    {showQuantity && (
                      sib._quantity_available
                        ? <td className="py-0.5 pl-1 text-right whitespace-nowrap"><span className="inline-block bg-[#F2E8D5] text-[#3a2d14] text-[9px] font-semibold rounded-sm px-1.5 py-0">{sib._quantity_display}</span></td>
                        : <td className="py-0.5 pl-1 text-right whitespace-nowrap"><span className="inline-block bg-[#F5F2EB] text-[#8a7a5c] text-[9px] rounded-sm px-1.5 py-0">Qty unavailable</span></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#E5E0D8]">
            {product.size && <span className="text-[10px] text-[#5C5C5C]">{product.size}</span>}
            <div className="flex items-center gap-1.5 ml-auto">
              {product._display_price && <span className="text-sm font-medium text-[#1A1A1A]">{product._display_price}</span>}
              {showQuantity && (
                product._quantity_available
                  ? <span className="inline-block bg-[#F2E8D5] text-[#3a2d14] text-[10px] font-semibold rounded-sm px-2 py-0.5">{product._quantity_display}</span>
                  : <span className="inline-block bg-[#F5F2EB] text-[#8a7a5c] text-[10px] rounded-sm px-2 py-0.5">Qty unavailable</span>
              )}
            </div>
          </div>
        )}
        {isCorporate && showPrices && (
          tiers.length ? (
            <div className="mt-2 pt-2 border-t border-[#E5E0D8]">
              <p className="text-[8px] tracking-[0.15em] uppercase font-semibold mb-1" style={{ color: primaryColor }}>MOQ Pricing</p>
              <table className="w-full text-[9px] border-collapse">
                <thead>
                  <tr className="border-b" style={{ borderColor: primaryColor }}>
                    <th className="text-left py-0.5 pr-1 font-semibold text-[#1A1A1A]">MOQ</th>
                    <th className="text-left py-0.5 pr-1 font-semibold text-[#1A1A1A]">Price/pc</th>
                    {tiers.some(t => t.delivery_days) && <th className="text-left py-0.5 font-semibold text-[#1A1A1A]">Delivery</th>}
                  </tr>
                </thead>
                <tbody>
                  {tiers.slice(0, 5).map((t, i) => (
                    <tr key={`fb-tier-${t.moq_quantity}-${i}`} className="border-b border-[#F0EBE2]">
                      <td className="py-0.5 pr-1 font-semibold text-[#1A1A1A] whitespace-nowrap">{Number(t.moq_quantity || 0).toLocaleString()}</td>
                      <td className="py-0.5 pr-1 font-semibold whitespace-nowrap" style={{ color: '#4a3a1f' }}>{t.calculated_price_display}</td>
                      {tiers.some(tt => tt.delivery_days) && <td className="py-0.5 text-[8px] text-[#1A1A1A] whitespace-nowrap">{t.delivery_days_display || '—'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-2 pt-2 border-t border-[#E5E0D8] text-[9px] text-[#9E2A2B] italic">MOQ pricing not set</p>
          )
        )}
      </div>
      {!isCorporate && product.gallery_images?.length > 0 && (
        <div className="flex gap-1 mt-2">
          {product.gallery_images.slice(0, 3).map((img) => (
            <img key={img} src={getImageUrl(img)} alt="" className="w-10 h-10 object-cover rounded-sm border border-[#E5E0D8]" />
          ))}
        </div>
      )}
    </div>
  );
}

function CorporateTermsPage({ meta, primaryColor }) {
  const m = meta || {};
  const defaultTerms = (m.default_terms || 'Prices are indicative and subject to confirmation at the time of order.').trim();
  const lines = [
    m.client_company && ['Client', m.client_company],
    m.contact_person && ['Contact', m.contact_person],
    m.validity_date && ['Valid until', m.validity_date],
    m.delivery_terms && ['Delivery', m.delivery_terms],
    m.gst_note && ['GST / Tax', m.gst_note],
  ].filter(Boolean);
  return (
    <div className="w-full h-full bg-[#FDFBF7] p-8 overflow-hidden">
      <h2 className="font-serif text-xl tracking-[0.3em] uppercase text-center mb-6" style={{ color: primaryColor }}>Terms</h2>
      {lines.length > 0 && (
        <div className="bg-white border border-[#E5E0D8] p-4 mb-4">
          {lines.map(([k, v]) => (
            <p key={k} className="text-[10px] text-[#1A1A1A] mb-1.5 last:mb-0 flex gap-2">
              <span className="uppercase tracking-wider text-[8px] w-20 shrink-0 pt-0.5" style={{ color: primaryColor }}>{k}</span>
              <span className="flex-1 min-w-0 break-words">{v}</span>
            </p>
          ))}
        </div>
      )}
      <p className="text-[10px] italic text-center text-[#5C5C5C] leading-relaxed">{defaultTerms}</p>
      {m.custom_note && <p className="text-[10px] text-[#1A1A1A] bg-[#F2E8D5] border-l-2 mt-4 p-3 leading-relaxed" style={{ borderColor: primaryColor }}>{m.custom_note}</p>}
    </div>
  );
}

function FooterPage({ catalog }) {
  const branding = catalog.branding || {};
  const primaryColor = branding.primary_color || '#C5A059';
  return (
    <div className="w-full h-full bg-[#1A1A1A] flex flex-col items-center justify-center p-10 text-center">
      <p className="font-serif text-xl mb-4" style={{ color: primaryColor }}>{branding.company_name || catalog.title}</p>
      {catalog.footer_contact && <p className="text-xs text-[#E5E0D8] whitespace-pre-line leading-relaxed">{catalog.footer_contact}</p>}
    </div>
  );
}

export default function FlipbookPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const flipBookRef = useRef(null);
  const containerRef = useRef(null);
  const viewMode = searchParams.get('view') || 'flipbook';

  useEffect(() => {
    const fetchCatalog = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await publicApi.get(`/shared/${token}`);
        setCatalog(data);
      } catch (e) {
        devError('Flipbook load failed', e);
        const status = e.response?.status;
        if (status === 404) setError('Catalog not found. The link may have expired or been removed.');
        else if (e.code === 'ECONNABORTED') setError('Request timed out. Check your network and retry.');
        else setError('Unable to load flipbook. Please retry.');
      }
      setLoading(false);
    };
    fetchCatalog();
  }, [token]);

  const buildPages = useCallback(() => {
    if (!catalog) return [];
    const pages = [];
    const branding = catalog.branding || {};
    const primaryColor = branding.primary_color || '#C5A059';
    const isCorporate = catalog.catalog_type === 'corporate';
    const showPrices = catalog.show_prices !== false;
    const showQuantity = !!catalog.show_quantity;
    pages.push({ type: 'cover', key: 'cover' });
    pages.push({ type: 'toc', key: 'toc' });
    for (const section of (catalog.sections || [])) {
      pages.push({ type: 'section', name: section.name, nameTrans: section._name_trans || '', primaryColor, key: `section-${section.id}` });
      // Product Master V2 (iter24): iterate collapsed cards -- one page per
      // design group (with size table) OR one page per standalone product.
      for (const card of iterateSectionCards(section.products)) {
        const pageKey = card.isGroup ? `group-${section.id}-${card.product.design_group_id}` : `product-${section.id}-${card.product.id}`;
        pages.push({ type: 'product', product: card.product, isGroupCard: card.isGroup, primaryColor, isCorporate, showPrices, showQuantity, key: pageKey });
      }
    }
    if (isCorporate) pages.push({ type: 'corp-terms', meta: catalog.corporate_meta || {}, primaryColor, key: 'corp-terms' });
    if (catalog.footer_contact) pages.push({ type: 'footer', key: 'footer' });
    return pages;
  }, [catalog]);

  const pages = buildPages();
  const totalPages = pages.length;

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    await downloadPdfWithCache(`/shared/${token}/pdf`, catalog?.title || 'catalog', { publicRoute: true });
    setPdfBusy(false);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  if (loading) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><div className="text-center"><Loader2 size={32} className="mx-auto animate-spin text-[#C5A059]" /><p className="text-sm text-[#5C5C5C] mt-4">Loading flipbook…</p></div></div>;
  if (error) return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
      <div className="text-center max-w-sm">
        <p className="text-[#1A1A1A] text-base font-medium">{error || 'Unable to load catalog. Please retry.'}</p>
        <Button onClick={() => window.location.reload()} className="mt-6 bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="flipbook-retry-btn">Retry</Button>
        <a href={`/shared/${token}`} className="block mt-4 text-sm text-[#C5A059] underline">View as list instead</a>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="min-h-screen bg-[#1A1A1A] flex flex-col" data-testid="flipbook-page">
      {/* Toolbar */}
      <div className="bg-[#1A1A1A] border-b border-[#333] px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="font-serif text-lg text-[#C5A059]">{catalog?.title}</h2>
          <span className="text-xs text-[#5C5C5C]">{currentPage + 1} / {totalPages}</span>
        </div>
        <div className="flex items-center gap-2">
          <a href={`/shared/${token}`} className="text-xs text-[#5C5C5C] hover:text-[#C5A059] mr-2 transition-colors" data-testid="switch-grid-view">
            <Grid3X3 size={16} />
          </a>
          <Button size="sm" variant="outline" onClick={() => flipBookRef.current?.pageFlip()?.flipPrev()} className="rounded-sm border-[#333] text-[#E5E0D8] bg-transparent hover:bg-[#333] h-8" data-testid="flip-prev">
            <ChevronLeft size={16} />
          </Button>
          <Button size="sm" variant="outline" onClick={() => flipBookRef.current?.pageFlip()?.flipNext()} className="rounded-sm border-[#333] text-[#E5E0D8] bg-transparent hover:bg-[#333] h-8" data-testid="flip-next">
            <ChevronRight size={16} />
          </Button>
          <Button size="sm" variant="outline" onClick={toggleFullscreen} className="rounded-sm border-[#333] text-[#E5E0D8] bg-transparent hover:bg-[#333] h-8" data-testid="flip-fullscreen">
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </Button>
          <Button size="sm" onClick={downloadPdf} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm h-8 text-xs" data-testid="flip-download-pdf">
            <Download size={14} className="mr-1" />PDF
          </Button>
        </div>
      </div>

      {/* Flipbook */}
      <div className="flex-1 flex items-center justify-center py-8">
        <HTMLFlipBook
          ref={flipBookRef}
          width={400}
          height={560}
          size="stretch"
          minWidth={300}
          maxWidth={500}
          minHeight={420}
          maxHeight={700}
          showCover={true}
          maxShadowOpacity={0.3}
          mobileScrollSupport={true}
          onFlip={(e) => setCurrentPage(e.data)}
          className="shadow-2xl"
          style={{}}
          startPage={0}
          drawShadow={true}
          flippingTime={600}
          usePortrait={true}
          startZIndex={0}
          autoSize={true}
          clickEventForward={true}
          useMouseEvents={true}
          swipeDistance={30}
          showPageCorners={true}
          disableFlipByClick={false}
        >
          {pages.map((page, i) => (
            <div key={page.key || `page-${i}`} className="bg-[#FDFBF7] overflow-hidden" data-testid={`flipbook-page-${i}`}>
              {page.type === 'cover' && <CoverPage catalog={catalog} />}
              {page.type === 'toc' && <TocPage catalog={catalog} />}
              {page.type === 'section' && <SectionDividerPage name={page.name} nameTrans={page.nameTrans} primaryColor={page.primaryColor} />}
              {page.type === 'product' && <ProductPage product={page.product} isGroupCard={page.isGroupCard} primaryColor={page.primaryColor} isCorporate={page.isCorporate} showPrices={page.showPrices} />}
              {page.type === 'corp-terms' && <CorporateTermsPage meta={page.meta} primaryColor={page.primaryColor} />}
              {page.type === 'footer' && <FooterPage catalog={catalog} />}
            </div>
          ))}
        </HTMLFlipBook>
      </div>
    </div>
  );
}
